import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { shallowEqual } from "react-redux";
import { Card, Select, Button, Spin } from "antd";
import {
  FileTextOutlined,
  DollarOutlined,
  GiftOutlined,
  ReadOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import API from "../../../../app/helpers/devApi";
import "./PayslipWidget.css";

const { Option } = Select;

const months = [
  { value: "all", label: "All Months" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function getYears() {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  }));
}

const typeConfig = {
  salary: { icon: DollarOutlined, label: "Salary" },
  bonus: { icon: GiftOutlined, label: "Bonus" },
  course: { icon: ReadOutlined, label: "Course" },
};

function formatAmount(amount) {
  if (amount == null) return "—";
  const num = Number(amount);
  if (Number.isNaN(num)) return String(amount);
  return num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function PayslipWidget() {
  const { auth } = useSelector(({ auth }) => ({ auth: auth.coreModuleInfo }), shallowEqual);
  const employeeId = auth?.employee_id;

  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [downloadingId, setDownloadingId] = useState(null);
  const [revealedAmountKeys, setRevealedAmountKeys] = useState(() => new Set());

  const years = getYears();

  const getSlipKey = (slip, idx) => {
    const k = slip.salary_id ?? slip.id ?? slip.date;
    if (k != null && k !== "") return String(k);
    return idx != null ? `row-${idx}` : "";
  };

  const toggleAmountVisible = (slip, idx) => {
    const key = getSlipKey(slip, idx);
    if (!key) return;
    setRevealedAmountKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (!employeeId) {
      setPayslips([]);
      return;
    }
    const fetchPayslips = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = { year: selectedYear };
        if (selectedMonth !== "all") params.month = selectedMonth;
        const response = await API.get(`/attendify/payslips/dashboard/${employeeId}`, { params });
        const data = response?.data?.data ?? response?.data ?? [];
        setPayslips(Array.isArray(data) ? data : []);
        setRevealedAmountKeys(new Set());
      } catch (err) {
        setError(err?.message || "Failed to load payslips");
        setPayslips([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPayslips();
  }, [employeeId, selectedYear, selectedMonth]);

  const handleDownload = async (slip) => {
    const salaryId = slip.salary_id ?? slip.id;
    if (!salaryId) return;
    try {
      setDownloadingId(salaryId);
      const res = await API.get(
        `/hrms-payroll-service/disbursements/disbursement-payslip/${salaryId}`
      );
      const filePath = res?.data?.data?.filePath ?? res?.data?.filePath ?? res?.filePath;
      if (!filePath) {
        throw new Error("Payslip file path not returned");
      }
      const path = `hrms-payroll-service/${filePath.replace(/^\//, "")}`;
      const blobRes = await API.get(path, { responseType: "blob" });
      const blob = blobRes?.data ?? blobRes;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip_${salaryId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error("Payslip download failed:", err);
      setError(err?.message || "Failed to download payslip");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="payslip-widget">
      <div className="payslip-widget__card">
        <div className="payslip-widget__header">
          <div className="payslip-widget__filters">
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              placeholder="Year"
              style={{ minWidth: 100 }}
            >
              {years.map((y) => (
                <Option key={y.value} value={y.value}>
                  {y.label}
                </Option>
              ))}
            </Select>
            <Select
              value={selectedMonth}
              onChange={setSelectedMonth}
              placeholder="Month"
              style={{ minWidth: 120 }}
            >
              {months.map((m) => (
                <Option key={m.value} value={m.value}>
                  {m.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="payslip-widget__content">
          {error && <div className="payslip-widget__error">{error}</div>}
          {loading ? (
            <div className="payslip-widget__loading">
              <Spin tip="Loading payslips..." />
            </div>
          ) : (
            <div className="payslip-widget__list">
              {payslips.length > 0 ? (
                payslips.map((slip, idx) => {
                  const config = typeConfig[slip.type] || { icon: FileTextOutlined, label: slip.type || "Payslip" };
                  const Icon = config.icon;
                  const slipKey = getSlipKey(slip, idx);
                  const amountVisible = revealedAmountKeys.has(slipKey);
                  return (
                    <div key={slipKey} className="payslip-widget__item">
                      <div className="payslip-widget__item-left">
                        <div
                          className={`payslip-widget__icon-wrap payslip-widget__icon-${slip.type || "salary"}`}
                        >
                          <Icon />
                        </div>
                        <div className="payslip-widget__item-details">
                          <p className="payslip-widget__item-date">{slip.date}</p>
                          <span className="payslip-widget__type-badge">
                            {config.label}
                          </span>
                          {slip.status && (
                            <span className="payslip-widget__status-badge">{slip.status}</span>
                          )}
                        </div>
                      </div>
                      <div className="payslip-widget__item-right">
                        <span
                          className={`payslip-widget__amount ${amountVisible ? "" : "payslip-widget__amount--masked"}`}
                        >
                          {amountVisible ? formatAmount(slip.amount) : "••••••"}
                        </span>
                        <Button
                          type="text"
                          size="small"
                          icon={amountVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                          className="payslip-widget__reveal-amount-btn"
                          aria-label={amountVisible ? "Hide amount" : "Show amount"}
                          onClick={() => toggleAmountVisible(slip, idx)}
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={<DownloadOutlined />}
                          className="payslip-widget__download-btn"
                          loading={downloadingId === (slip.salary_id ?? slip.id)}
                          disabled={!!downloadingId}
                          onClick={() => handleDownload(slip)}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="payslip-widget__empty">
                  No payslips found for the selected period.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
